<template>
  <Teleport to="body">
    <div v-bind="$attrs" role="dialog" aria-modal="true" class="agenda-modal">
      <div class="agenda-modal-overlay" ref="overlay" @click="close"></div>
      <div class="agenda-modal-panel" ref="panel">
        <header class="agenda-modal-header">
          <div class="agenda-modal-title-block">
            <slot name="title"></slot>
          </div>
          <button class="agenda-modal-close" @click="close" aria-label="Close">
            <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </header>

        <div class="agenda-modal-controls" v-if="$slots.controls || $scopedSlots.controls">
          <slot name="controls"></slot>
        </div>

        <div class="agenda-modal-body" ref="body">
          <slot></slot>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import Teleport from 'vue2-teleport';
import swipeToDismiss from './swipe-to-dismiss';

export default {
  name: 'lp-modal',
  inheritAttrs: false,
  mixins: [swipeToDismiss],
  components: {
    Teleport,
  },
  methods: {
    close() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss">
/* ============ AGENDA MODAL ============ */
/* Structural shell rendered by <lp-modal>. Styling (incl. the 24px panel
   radius) lives here so the modal renders identically on every page that
   uses this component — the landing page and the registration take-over. */
.agenda-modal {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
}
.agenda-modal[hidden] { display: none; }
.agenda-modal-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.4);
  backdrop-filter: blur(4px);
}
.agenda-modal-panel {
  position: relative;
  width: 100%;
  max-width: 1100px;
  height: 100%;
  max-height: calc(100vh - 64px);
  background: var(--bg);
  border-radius: 24px;
  box-shadow: var(--shadow-xl);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.agenda-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 28px 32px 0;
  flex-shrink: 0;
}
.agenda-modal-title-block { flex: 1; }
.agenda-modal-close {
  background: transparent;
  border: 0.5px solid var(--border);
  color: var(--text-secondary);
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.agenda-modal-close:hover { background: var(--surface); border-color: var(--border-strong); color: var(--text); }

.agenda-modal-controls {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 20px 32px 16px;
  border-bottom: 0.5px solid var(--border);
  flex-shrink: 0;
  flex-wrap: wrap;
}
.agenda-modal-body {
  flex: 1;
  // A flex item's automatic minimum size is its content, which on mobile Safari
  // keeps this from shrinking inside the sheet — it then overflows (clipped by
  // the panel) instead of scrolling. min-height: 0 lets it shrink and scroll.
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain; // don't hand the scroll to the page behind
  -webkit-overflow-scrolling: touch;
  padding: 24px 32px 32px;
}

@media (max-width: 800px) {
  .agenda-modal { padding: 16px; }
  .agenda-modal-panel { max-height: calc(100vh - 32px); }
  .agenda-modal-header { padding: 20px 20px 0; }
  .agenda-modal-controls { padding: 16px 20px; }
  .agenda-modal-body { padding: 20px; }
}

/* ---- Mobile: modal → bottom sheet ---- */
@keyframes sheetUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
@media (max-width: 640px) {
  .agenda-modal {
    align-items: flex-end;
    padding: 0;
  }
  .agenda-modal-overlay { background: rgba(0, 0, 0, 0.45); }
  .agenda-modal-panel {
    width: 100%;
    max-width: 100%;
    height: auto;
    max-height: 90%;
    border-radius: 24px 24px 0 0;
    animation: sheetUp 0.28s cubic-bezier(0.2, 0.7, 0.2, 1);

    &::before {
      content: "";
      flex-shrink: 0;
      width: 38px;
      height: 5px;
      border-radius: 3px;
      background: var(--border);
      margin: 10px auto 2px;
    }
  }
  .agenda-modal-header { padding: 6px 20px 14px; }
  .agenda-modal-controls { padding: 0 20px 14px; }
  .agenda-modal-body { padding: 18px 20px 28px; }
}
</style>
